import React, { useEffect, useRef, useState } from 'react';
import { ContentProfileData } from '@/api/entities';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, ServerCrash } from 'lucide-react';

const NOT_ESTABLISHED = 'Not established';

function isPresentArray(value) {
  return Array.isArray(value) && value.length > 0;
}

function ChipRow({ label, values, neutral = false }) {
  if (!isPresentArray(values)) {
    return (
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">{label}</p>
        <p className="text-sm text-[#5C6B7A]">{NOT_ESTABLISHED}</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {values.map((item) => (
          <Badge
            key={String(item)}
            variant="secondary"
            className={
              neutral
                ? 'bg-gray-100 text-gray-700 border border-gray-200 font-normal'
                : 'bg-[#ECFFFE] text-[#0A1A2F] border border-[#B8F0F0] font-normal'
            }
          >
            {item}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function ProseBlock({ label, text, quiet = false }) {
  if (text == null || String(text).trim() === '') return null;
  return (
    <div className="space-y-1.5">
      {label ? (
        <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">{label}</p>
      ) : null}
      <p className={`leading-relaxed whitespace-pre-wrap ${quiet ? 'text-sm text-[#5C6B7A]' : 'text-sm text-[#0A1A2F]'}`}>
        {text}
      </p>
    </div>
  );
}

export default function ContentProfile({ accountName, accountId, profileData: profileDataProp }) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const profileDataPropRef = useRef(profileDataProp);
  profileDataPropRef.current = profileDataProp;

  useEffect(() => {
    // Parent finished load: object or explicit null both mean stop waiting on parent.
    if (profileDataProp !== undefined) {
      setProfile(profileDataProp || null);
      setIsLoading(false);
      setError(null);
    }
  }, [profileDataProp]);

  useEffect(() => {
    if (profileDataProp !== undefined) return;
    if (!accountName && !accountId) {
      setIsLoading(false);
      setError(null);
      setProfile(null);
      return;
    }

    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let results = [];
        if (accountId) {
          results = await ContentProfileData.filter({ account_id: accountId }).catch(() => []);
        }
        if ((!results || results.length === 0) && accountName) {
          results = await ContentProfileData.filter({ account_name: accountName }).catch(() => []);
        }
        if (profileDataPropRef.current !== undefined) return;
        setProfile(results?.[0] || null);
      } catch (err) {
        console.error('Failed to fetch content profile:', err);
        if (profileDataPropRef.current === undefined) {
          setError('Could not load content profile.');
          setProfile(null);
        }
      } finally {
        if (profileDataPropRef.current === undefined) setIsLoading(false);
      }
    };

    fetchProfile();
  }, [accountName, accountId, profileDataProp]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        <ServerCrash className="w-8 h-8 mx-auto mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-10">
        <p className="text-[#5C6B7A]">Content not yet profiled for this account.</p>
      </div>
    );
  }

  const noEvidence = profile.profile_status === 'No explicit content evidence';

  return (
    <div className="space-y-4 md:space-y-5">
      {noEvidence ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 md:p-5">
            <p className="text-sm text-[#5C6B7A] leading-relaxed">
              No explicit content evidence was found for this organisation. That is a status of the
              sweep, not a claim that they offer nothing.
            </p>
            <ProseBlock label="Unknowns" text={profile.unknowns_note} quiet />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 px-4 md:px-5 pt-4 md:pt-5">
              <CardTitle className="text-base text-[#0A1A2F]">Summary</CardTitle>
            </CardHeader>
            <CardContent className="px-4 md:px-5 pb-4 md:pb-5 space-y-3">
              <ProseBlock text={profile.content_summary} />
              <ProseBlock label="Key evidence" text={profile.key_evidence} quiet />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 px-4 md:px-5 pt-4 md:pt-5">
              <CardTitle className="text-base text-[#0A1A2F]">What they offer</CardTitle>
            </CardHeader>
            <CardContent className="px-4 md:px-5 pb-4 md:pb-5 space-y-4">
              <ChipRow label="Formats" values={profile.formats} />
              <ChipRow label="Topics" values={profile.topics} />
              <ChipRow label="Audiences" values={profile.audiences} />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 px-4 md:px-5 pt-4 md:pt-5">
              <CardTitle className="text-base text-[#0A1A2F]">How it is accessed</CardTitle>
            </CardHeader>
            <CardContent className="px-4 md:px-5 pb-4 md:pb-5 space-y-4">
              <ChipRow label="Access models" values={profile.access_models} />
              <ChipRow label="Accessibility features" values={profile.accessibility_features} />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 px-4 md:px-5 pt-4 md:pt-5">
              <CardTitle className="text-base text-[#0A1A2F]">Platforms in place</CardTitle>
            </CardHeader>
            <CardContent className="px-4 md:px-5 pb-4 md:pb-5 space-y-3">
              <ChipRow label="Platforms" values={profile.platforms} neutral />
              <ProseBlock label="Provider stack" text={profile.provider_stack_detail} quiet />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 px-4 md:px-5 pt-4 md:pt-5">
              <CardTitle className="text-base text-[#0A1A2F]">Scale</CardTitle>
            </CardHeader>
            <CardContent className="px-4 md:px-5 pb-4 md:pb-5 space-y-4">
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">Title counts</p>
                {profile.title_counts ? (
                  <>
                    <p className="text-sm text-[#0A1A2F] whitespace-pre-wrap">{profile.title_counts}</p>
                    {profile.title_count_detail ? (
                      <p className="text-sm text-[#5C6B7A] whitespace-pre-wrap">{profile.title_count_detail}</p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-[#5C6B7A]">{NOT_ESTABLISHED}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <ChipRow label="Languages" values={profile.languages} />
                {profile.language_count_claims ? (
                  <p className="text-sm text-[#5C6B7A] whitespace-pre-wrap">{profile.language_count_claims}</p>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 px-4 md:px-5 pt-4 md:pt-5">
              <CardTitle className="text-base text-[#0A1A2F]">Gaps and signals</CardTitle>
            </CardHeader>
            <CardContent className="px-4 md:px-5 pb-4 md:pb-5 space-y-4">
              {profile.content_gaps ? (
                <ProseBlock label="Content gaps" text={profile.content_gaps} quiet />
              ) : (
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">Content gaps</p>
                  <p className="text-sm text-[#5C6B7A]">{NOT_ESTABLISHED}</p>
                </div>
              )}
              <ChipRow label="Relevant signals" values={profile.relevant_signals} />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 px-4 md:px-5 pt-4 md:pt-5">
              <CardTitle className="text-base text-[#0A1A2F]">Evidence</CardTitle>
            </CardHeader>
            <CardContent className="px-4 md:px-5 pb-4 md:pb-5 space-y-3">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#5C6B7A]">
                {profile.evidence_row_count != null ? <span>{profile.evidence_row_count} evidence rows</span> : null}
                {profile.verified_provider_rows != null ? (
                  <span>{profile.verified_provider_rows} verified provider rows</span>
                ) : null}
                {profile.pdf_evidence_rows != null ? <span>{profile.pdf_evidence_rows} PDF evidence rows</span> : null}
              </div>
              {isPresentArray(profile.source_urls) ? (
                <ul className="space-y-1.5">
                  {profile.source_urls.map((url) => (
                    <li key={url}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#00C2C2] hover:text-[#00A3A3] hover:underline inline-flex items-center gap-1 break-all"
                      >
                        {url}
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#5C6B7A]">{NOT_ESTABLISHED}</p>
              )}
              <ProseBlock label="Unknowns" text={profile.unknowns_note} quiet />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
